import { createLogger } from '@/lib/logger';
import { type CartEventDetailMap, type CartEventName } from './core';

/**
 * Lightweight event bus that proxies CustomEvents on `window`.
 *
 * - Names are defined in `CartEvents`.
 * - `emit` attaches `external` to the detail payload to indicate the initiator.
 *   By default `external` is `false` for events emitted by the mini-cart internals.
 */
export class CartEventBus {
  private logger = createLogger('cart-event-bus');
  /**
   * Register an event listener.
   * @param name Event name from `CartEvents`.
   * @param listener Callback receiving the CustomEvent.
   * @param opts Optional AddEventListenerOptions (e.g. `{ signal }`).
   */
  on<N extends CartEventName>(
    name: N,
    listener: (event: CustomEvent<CartEventDetailMap[N]>) => void,
    opts?: AddEventListenerOptions,
  ): void {
    this.logger.info(`Adding listener for ${name}`, listener);
    window.addEventListener(name, listener as EventListener, opts);
  }

  /**
   * Register multiple event listeners.
   * @param names Array of event names from `CartEvents`.
   * @param listener Callback receiving the CustomEvent.
   * @param opts Optional AddEventListenerOptions (e.g. `{ signal }`).
   */
  onMany<N extends CartEventName>(
    names: N[],
    listener: (event: CustomEvent<CartEventDetailMap[N]>) => void,
    opts?: AddEventListenerOptions,
  ): void {
    names.forEach((name) => this.on(name, listener, opts));
  }

  /**
   * Remove an event listener.
   */
  off<N extends CartEventName>(
    name: N,
    listener: (event: CustomEvent<CartEventDetailMap[N]>) => void,
    opts?: EventListenerOptions,
  ): void {
    this.logger.info(`Removing listener for ${name}`, listener);
    window.removeEventListener(name, listener as EventListener, opts);
  }

  /**
   * Emit a CustomEvent on `window` with optional payload.
   * @param name Event name from `CartEvents`.
   * @param detail Optional payload that will be spread into `event.detail`.
   * @param external Whether the initiator is external to the mini-cart internals. Defaults to `false`.
   */
  emit<N extends CartEventName>(
    name: N,
    detail?: CartEventDetailMap[N],
    external: boolean = false,
  ): void {
    this.logger.info(`Emitting ${name}${external ? ' (external initiator)' : ''}`, detail);
    const evt = new CustomEvent(name, { detail: { ...detail, external } });
    window.dispatchEvent(evt);
  }
}
